import { useCallback, useMemo } from 'react';
import { useGetWorkTypeCatalogQuery } from 'redux/apiSlices/workType.slice';

/**
 * Resolves a work type key to its display name. The API sends keys, never names.
 *
 * Reads the full catalog rather than the active-only list, because a retired work type
 * still has to render its name on historical time entries and reports. Dropdowns are a
 * separate concern and build their options from the active list instead.
 *
 * Falls back to the key when it can't be resolved, so an unknown work type shows
 * something intelligible rather than blank.
 */
export const useWorkTypeNames = () => {
  const {
    data: catalog,
    isLoading,
    isUninitialized,
  } = useGetWorkTypeCatalogQuery();

  // A subscribed query is uninitialized rather than loading on its very first render, and that
  // is the render a raw key would be painted on, so both states count as not yet resolved.
  const isResolving = isLoading || isUninitialized;

  // builds in one pass — this runs per hook instance, and WorkTypeName mounts once per
  // table row, so spreading the accumulator here would rebuild the whole map per entry
  const namesByKey = useMemo(() => {
    const names: Record<string, string> = {};
    (catalog || []).forEach(workType => {
      names[workType.key] = workType.name;
    });
    return names;
  }, [catalog]);

  /**
   * Resolves a key, falling back to the key itself only once the request has settled.
   *
   * While the catalog is still in flight a key is not unresolvable, just unresolved, and
   * returning it paints a raw value — a UUID, for a work type an MSP created itself — which
   * then corrects itself a moment later. Callers render nothing for that moment instead.
   *
   * A failed request still falls back to the key, so losing the catalog degrades to keys
   * rather than to blanks.
   */
  // stable across renders so callers can safely use it as a dependency
  const nameOf = useCallback(
    (key?: string) => {
      if (!key) return '';
      return namesByKey[key] || (isResolving ? '' : key);
    },
    [namesByKey, isResolving],
  );

  return { nameOf, isResolving };
};

export default useWorkTypeNames;
